import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { doc, onSnapshot, collection, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Box, Typography, CircularProgress, Divider, IconButton, Slider } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TuneIcon from '@mui/icons-material/Tune';
import WordCloud from '../components/WordCloud';

const accent = '#E05500';

// ── Hex → rgba ────────────────────────────────────────────────────────────────
function hexToRgba(hex, alpha) {
  if (!hex || !hex.startsWith('#')) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (isNaN(r)) return `rgba(255,255,255,${alpha})`;
  return `rgba(${r},${g},${b},${+alpha.toFixed(3)})`;
}

// ── Glasspanel-stil (dynamisk transparens + blur) ──────────────────────────────
function makeGlass(color, panelOpacity, panelBlur) {
  const op = panelOpacity / 100;
  return {
    background: color
      ? `linear-gradient(135deg, ${hexToRgba(color, op * 1.5)} 0%, ${hexToRgba(color, op * 0.5)} 100%)`
      : `rgba(255,255,255,${op})`,
    backdropFilter: `blur(${panelBlur}px) saturate(160%)`,
    WebkitBackdropFilter: `blur(${panelBlur}px) saturate(160%)`,
    border: `1px solid ${color ? hexToRgba(color, 0.25) : `rgba(255,255,255,${Math.min(op * 2, 0.25)})`}`,
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
  };
}

// ── Grid: sikta på 2 rader, 2–4 kolumner ─────────────────────────────────────
function gridConfig(totalSlots) {
  const cols = Math.min(4, Math.max(2, Math.ceil(totalSlots / 2)));
  const fontScale = cols === 2 ? 1.1 : cols === 3 ? 0.92 : 0.78;
  return { cols, fontScale };
}

// ── Svarslista ────────────────────────────────────────────────────────────────
function AnswerList({ answers, scale }) {
  return (
    <Box sx={{ columnCount: 2, columnFill: 'auto', columnGap: '12px', height: '100%' }}>
      {answers.map((r, i) => (
        <Box key={i} sx={{ breakInside: 'avoid', borderBottom: '1px solid rgba(255,255,255,0.08)', pb: 0.4, mb: 0.4 }}>
          <Typography sx={{ fontSize: `${scale * 0.88}rem`, color: 'rgba(255,255,255,0.9)', lineHeight: 1.5, py: 0.3 }}>
            {r.answer}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Grouppanel ────────────────────────────────────────────────────────────────
function GroupPanel({ group, answers, scale, panelOpacity, panelBlur }) {
  return (
    <Box sx={{ ...makeGlass(group.color, panelOpacity, panelBlur), p: `${scale * 14}px`, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header: namnchip + svarsräknare */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75,
          bgcolor: hexToRgba(group.color || '#ffffff', 0.38),
          border: `1px solid ${hexToRgba(group.color || '#ffffff', 0.65)}`,
          borderRadius: '10px',
          px: 1, py: 0.35,
        }}>
          <Box sx={{ width: scale * 9, height: scale * 9, borderRadius: '50%', bgcolor: group.color || accent, flexShrink: 0, boxShadow: `0 0 6px ${group.color || accent}99` }} />
          <Typography sx={{ fontWeight: 800, fontSize: `${scale * 0.95}rem`, color: '#fff', letterSpacing: 0.3, lineHeight: 1 }}>
            {group.name}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', px: 0.9, py: 0.25 }}>
          <Typography sx={{ fontSize: `${scale * 0.7}rem`, color: 'rgba(255,255,255,0.8)', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
            {answers.length} svar
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: hexToRgba(group.color || '#ffffff', 0.2), mb: 1, flexShrink: 0 }} />

      <Box sx={{ flex: 1, overflow: 'hidden', height: 0 }}>
        {answers.length === 0 ? (
          <Typography sx={{ fontSize: `${scale * 0.75}rem`, color: 'rgba(255,255,255,0.22)', fontStyle: 'italic' }}>
            Inga svar ännu...
          </Typography>
        ) : (
          <AnswerList answers={answers} scale={scale} />
        )}
      </Box>
    </Box>
  );
}

// ── Nav-pil ───────────────────────────────────────────────────────────────────
function NavArrow({ direction, onClick, disabled }) {
  return (
    <Box sx={{ position: 'absolute', top: '50%', [direction === 'left' ? 'left' : 'right']: 14, transform: 'translateY(-50%)', zIndex: 10 }}>
      <IconButton
        onClick={onClick}
        disabled={disabled}
        sx={{
          width: 52, height: 52,
          background: disabled ? 'rgba(255,255,255,0.04)' : 'rgba(224,85,0,0.18)',
          border: `2px solid ${disabled ? 'rgba(255,255,255,0.08)' : accent + 'aa'}`,
          borderRadius: '50%',
          color: disabled ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(12px)',
          transition: 'all 0.2s',
          '&:hover:not(:disabled)': {
            background: `rgba(224,85,0,0.45)`,
            border: `2px solid ${accent}`,
            color: '#fff',
            boxShadow: `0 0 18px ${accent}88`,
          },
          '&.Mui-disabled': { opacity: 1 },
        }}
      >
        {direction === 'left' ? <ChevronLeftIcon sx={{ fontSize: 30 }} /> : <ChevronRightIcon sx={{ fontSize: 30 }} />}
      </IconButton>
    </Box>
  );
}

// ── Inställningspanel ─────────────────────────────────────────────────────────
function SettingsPanel({ panelOpacity, setPanelOpacity, panelBlur, setPanelBlur, bgBlur, setBgBlur, onClose }) {
  const rowSx = { mb: 2 };
  const labelRowSx = { display: 'flex', justifyContent: 'space-between', mb: 0.5 };
  const labelSx = { fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: 1 };
  const valSx   = { fontSize: '0.62rem', color: 'rgba(255,255,255,0.65)', fontWeight: 600 };
  const sliderSx = { color: accent, py: 0.5, '& .MuiSlider-thumb': { width: 14, height: 14 } };

  return (
    <Box sx={{
      background: 'rgba(10,10,10,0.92)',
      backdropFilter: 'blur(28px)',
      WebkitBackdropFilter: 'blur(28px)',
      border: '1px solid rgba(255,255,255,0.14)',
      borderRadius: '14px',
      boxShadow: '0 16px 48px rgba(0,0,0,0.7)',
      p: 2.5,
      width: 270,
    }}>
      <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.3)', fontWeight: 700, letterSpacing: 1.5, mb: 2 }}>
        UTSEENDE
      </Typography>

      <Box sx={rowSx}>
        <Box sx={labelRowSx}>
          <Typography sx={labelSx}>PANELTRANSPARENS</Typography>
          <Typography sx={valSx}>{panelOpacity}%</Typography>
        </Box>
        <Slider size="small" min={2} max={60} value={panelOpacity}
          onChange={(_, v) => setPanelOpacity(v)} sx={sliderSx} />
      </Box>

      <Box sx={rowSx}>
        <Box sx={labelRowSx}>
          <Typography sx={labelSx}>PANELOSKÄRPA</Typography>
          <Typography sx={valSx}>{panelBlur}px</Typography>
        </Box>
        <Slider size="small" min={0} max={40} value={panelBlur}
          onChange={(_, v) => setPanelBlur(v)} sx={sliderSx} />
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Box sx={labelRowSx}>
          <Typography sx={labelSx}>BAKGRUNDSOSKÄRPA</Typography>
          <Typography sx={valSx}>{bgBlur}px</Typography>
        </Box>
        <Slider size="small" min={0} max={30} value={bgBlur}
          onChange={(_, v) => setBgBlur(v)} sx={sliderSx} />
      </Box>

      <Box component="button" onClick={onClose} sx={{
        width: '100%', py: 0.65, mt: 0.5,
        borderRadius: '8px',
        border: '1px solid rgba(255,255,255,0.18)',
        bgcolor: 'transparent',
        color: 'rgba(255,255,255,0.55)',
        fontSize: '0.7rem',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: '#fff' },
      }}>
        Stäng
      </Box>
    </Box>
  );
}

// ── Embed-URL-parser (YouTube / Vimeo) ───────────────────────────────────────
function toEmbedUrl(url) {
  let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (m) {
    const id = m[1];
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&rel=0`;
  }
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}?autoplay=1&muted=1&loop=1&background=1`;
  return null;
}

// ── Kiosk-vy ──────────────────────────────────────────────────────────────────
export default function WorkshopNKiosk() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const bgParam = searchParams.get('bg');
  const bgFromStorage = bgParam === 'custom' ? (localStorage.getItem('workshopKioskBg') || null) : null;
  const bgSource = bgParam && bgParam !== 'custom' ? bgParam : bgFromStorage;

  const [session,      setSession]      = useState(null);
  const [responses,    setResponses]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [currentQ,     setCurrentQ]     = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Utseendeinställningar (sparas i localStorage)
  const [panelOpacity, setPanelOpacityRaw] = useState(() => parseFloat(localStorage.getItem('kioskPanelOpacity') || '7'));
  const [panelBlur,    setPanelBlurRaw]    = useState(() => parseFloat(localStorage.getItem('kioskPanelBlur')    || '24'));
  const [bgBlur,       setBgBlurRaw]       = useState(() => parseFloat(localStorage.getItem('kioskBgBlur')       || '8'));

  const setPanelOpacity = v => { setPanelOpacityRaw(v); localStorage.setItem('kioskPanelOpacity', String(v)); };
  const setPanelBlur    = v => { setPanelBlurRaw(v);    localStorage.setItem('kioskPanelBlur',    String(v)); };
  const setBgBlur       = v => { setBgBlurRaw(v);       localStorage.setItem('kioskBgBlur',       String(v)); };

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'workshops', id), snap => {
      if (!snap.exists()) return;
      const data = { id: snap.id, ...snap.data() };
      setSession(data);
      setCurrentQ(data.status?.currentQuestion ?? 0);
      setLoading(false);
    });
    return unsub;
  }, [id]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'workshops', id, 'responses'), snap => {
      setResponses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [id]);

  const questions = useMemo(() => session?.config?.questions || [], [session]);
  const groups    = useMemo(() => session?.config?.groups    || [], [session]);
  const question  = questions[currentQ];

  const bgVideo    = !bgSource ? (session?.videoUrl || null) : null;
  const bgEmbedRaw = !bgSource && !session?.videoUrl ? (session?.heroEmbedUrl || null) : null;
  const bgEmbedSrc = bgEmbedRaw ? toEmbedUrl(bgEmbedRaw) : null;
  const bgImage    = bgSource || (!bgVideo && !bgEmbedSrc ? session?.imageUrl : null) || null;

  const showWordCloud = question?.type === 'ordmoln' || question?.type === 'fritext';
  const totalSlots = groups.length + (showWordCloud ? 1 : 0);
  const { cols, fontScale } = gridConfig(totalSlots);

  // Ordmolnet spänner över lediga kolumner i sin rad
  const wcColSpan = showWordCloud ? Math.max(1, cols - groups.length % cols) : 1;

  const goTo = (n) => {
    if (n < 0 || n >= questions.length) return;
    setCurrentQ(n);
    updateDoc(doc(db, 'workshops', id), {
      status: { ...(session?.status || {}), currentQuestion: n },
      updatedAt: serverTimestamp(),
    });
  };

  const responsesByGroup = useMemo(() => {
    const map = {};
    for (const g of groups) {
      map[g.id] = responses
        .filter(r => r.groupId === g.id && r.answers?.[currentQ] !== undefined && r.answers?.[currentQ] !== '')
        .map(r => ({ answer: r.answers[currentQ] }));
    }
    return map;
  }, [groups, responses, currentQ]);

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative', bgcolor: '#0a0a0a', userSelect: 'none' }}>

      {/* Bakgrundsvideo */}
      {bgVideo && (
        <Box component="video" src={bgVideo} autoPlay muted loop playsInline
          sx={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover',
            filter: `blur(${bgBlur}px) brightness(0.42) saturate(1.3)`,
            transform: 'scale(1.08)',
          }}
        />
      )}
      {/* Bakgrundsinbäddning (YouTube / Vimeo) */}
      {!bgVideo && bgEmbedSrc && (
        <Box component="iframe" src={bgEmbedSrc} title="bg" allow="autoplay; fullscreen"
          sx={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            border: 'none', pointerEvents: 'none',
            filter: `blur(${bgBlur}px) brightness(0.42) saturate(1.3)`,
            transform: 'scale(1.08)',
          }}
        />
      )}
      {/* Bakgrundsbild */}
      {!bgVideo && !bgEmbedSrc && bgImage && (
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `url("${bgImage}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: `blur(${bgBlur}px) brightness(0.42) saturate(1.3)`,
          transform: 'scale(1.08)',
        }} />
      )}
      <Box sx={{ position: 'absolute', inset: 0, background: (bgVideo || bgEmbedSrc || bgImage)
        ? 'linear-gradient(180deg,rgba(0,0,0,.38) 0%,rgba(0,0,0,.15) 50%,rgba(0,0,0,.45) 100%)'
        : 'linear-gradient(135deg,#0d0d0d 0%,#1a0d00 100%)' }} />

      {/* Nav-pilar */}
      <NavArrow direction="left"  onClick={() => goTo(currentQ - 1)} disabled={currentQ === 0} />
      <NavArrow direction="right" onClick={() => goTo(currentQ + 1)} disabled={currentQ >= questions.length - 1} />

      {/* Utseendeinställningar — gear-ikon nere till höger */}
      <Box sx={{ position: 'absolute', bottom: 18, right: 18, zIndex: 20 }}>
        {settingsOpen && (
          <Box sx={{ position: 'absolute', bottom: 'calc(100% + 10px)', right: 0 }}>
            <SettingsPanel
              panelOpacity={panelOpacity} setPanelOpacity={setPanelOpacity}
              panelBlur={panelBlur}       setPanelBlur={setPanelBlur}
              bgBlur={bgBlur}             setBgBlur={setBgBlur}
              onClose={() => setSettingsOpen(false)}
            />
          </Box>
        )}
        <IconButton
          onClick={() => setSettingsOpen(o => !o)}
          sx={{
            width: 40, height: 40,
            bgcolor: settingsOpen ? accent : 'rgba(255,255,255,0.08)',
            color: settingsOpen ? '#fff' : 'rgba(255,255,255,0.45)',
            border: `1px solid ${settingsOpen ? accent : 'rgba(255,255,255,0.15)'}`,
            backdropFilter: 'blur(12px)',
            transition: 'all 0.2s',
            '&:hover': { bgcolor: settingsOpen ? '#c44300' : 'rgba(255,255,255,0.16)', color: '#fff' },
          }}
        >
          <TuneIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Innehåll */}
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100vh', px: { xs: 8, md: 10 }, py: { xs: 1.5, md: 2.5 }, gap: 1.5 }}>

        {/* Header */}
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end', gap: 3 }}>
          <Box>
            <Typography sx={{ fontSize: `${fontScale * 0.65}rem`, color: accent, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', mb: 0.3 }}>
              {session?.title}
            </Typography>
            {/* Frågetext + underrubrik på samma rad */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: `${fontScale * 1.45}rem`, fontWeight: 900, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.5px', textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}>
                {question?.text}
              </Typography>
              {question?.description && (
                <Typography sx={{ fontSize: `${fontScale * 0.82}rem`, fontWeight: 400, color: 'rgba(255,255,255,0.52)', lineHeight: 1.2 }}>
                  {question.description}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ flex: 1 }} />
          {/* Frågedots */}
          <Box sx={{ ...makeGlass(null, panelOpacity, panelBlur), px: 2, py: 0.75, flexShrink: 0, display: 'flex', gap: 0.75, alignItems: 'center' }}>
            {questions.map((_, i) => (
              <Box key={i} onClick={() => goTo(i)} sx={{
                width: i === currentQ ? 22 : 8, height: 8,
                borderRadius: 4,
                bgcolor: i === currentQ ? accent : 'rgba(255,255,255,0.22)',
                transition: 'all 0.3s', cursor: 'pointer',
                '&:hover': { bgcolor: i === currentQ ? accent : 'rgba(255,255,255,0.5)' },
              }} />
            ))}
          </Box>
        </Box>

        {/* Grid: grupper + ordmoln */}
        <Box sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridAutoRows: '1fr',
          gap: 1.5,
          overflow: 'hidden',
          minHeight: 0,
        }}>
          {groups.map(g => (
            <GroupPanel
              key={g.id}
              group={g}
              answers={responsesByGroup[g.id] || []}
              scale={fontScale}
              panelOpacity={panelOpacity}
              panelBlur={panelBlur}
            />
          ))}
          {showWordCloud && (
            <Box sx={{
              ...makeGlass(null, panelOpacity, panelBlur),
              gridColumn: `span ${wcColSpan}`,
              p: 1.5,
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}>
              <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.3)', fontWeight: 700, letterSpacing: 1.5, mb: 0.75, flexShrink: 0 }}>
                ORDMOLN
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <WordCloud
                  key={currentQ}
                  responses={responses}
                  questionIndex={currentQ}
                  initialScheme="dark"
                />
              </Box>
            </Box>
          )}
        </Box>

      </Box>
    </Box>
  );
}
